import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@app/core/i18n';
import { PatternFormat, PatternFormatProps } from 'react-number-format';

import { Input } from '@ast/magma/components/input';

import { CountrySelectorControl } from '@app/common/components/controls/CountrySelectorControl/CountrySelectorControl';
import { DEFAULT_COUNTRY } from '@app/common/types/locales';
import { Country, findCountryByCode, findCountryByPhone } from '@app/common/utils/countries';
import { useIsMobile } from '@app/common/utils/hooks/useIsMobile';

import styles from './InternationalPhoneControl.pcss';

/**
 * Component props.
 */
export interface InternationalPhoneControlProps {
  // eslint-disable-next-line i18next/no-literal-string
  readonly 'aria-invalid'?: boolean;
  readonly format: PatternFormatProps['format'];
  readonly defaultValue?: string;
  readonly onChange?: (value: string) => void;
  readonly onCountryChange?: (country: Country) => void;
}

/**
 * Represents the PhoneControl component.
 * Used for contacts and settings phone number.
 */
export const InternationalPhoneControl: React.FC<InternationalPhoneControlProps> = ({
  format,
  defaultValue,
  onChange,
  onCountryChange,
  // eslint-disable-next-line i18next/no-literal-string
  'aria-invalid': invalid,
}) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('');
  const isMobile = useIsMobile();

  const defaultCountry = useMemo(() => (
    defaultValue
      ? findCountryByPhone(defaultValue)
      : findCountryByCode(DEFAULT_COUNTRY)
  ), []);

  const defaultPhone = defaultCountry
    ? defaultValue?.replace(defaultCountry.dialCode, '')
    : defaultValue;

  useEffect(() => {
    if (defaultCountry) {
      setDialCode(defaultCountry.dialCode);
    }

    if (defaultPhone) {
      setPhone(defaultPhone);
    }
  }, []);

  return (
    <div className={styles.wrapper}>
      <CountrySelectorControl
        className={styles.countryCode}
        defaultValue={defaultCountry}
        dropVerticalAlignment={isMobile ? 'top' : 'bottom'}
        onChange={(item: Country) => {
          setDialCode(item.dialCode);

          if (onChange) {
            onChange(`${item.dialCode}${phone}`);
          }

          if (onCountryChange) {
            onCountryChange(item);
          }
        }}
      />
      <PatternFormat
        data-stable-name="PhoneNumber"
        className={styles.phoneNumber}
        aria-invalid={invalid}
        mask=""
        type="tel"
        label={t(
          'international-phone-control.phone-input.label|Phone number input label',
          'Phone number',
        )}
        customInput={Input}
        format={format}
        defaultValue={defaultPhone}
        onValueChange={({ formattedValue }) => {
          setPhone(formattedValue);

          if (onChange) {
            onChange(`${dialCode}${formattedValue}`);
          }
        }}
      />
    </div>
  );
};
